import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuthCtx } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2, ShieldX, LogOut, Smartphone } from "lucide-react";

import SignIn from "@/auth/SignIn";
import NotAuthorized from "@/auth/NotAuthorized";

import AttendeeLayout from "@/attendee/AttendeeLayout";
import Home from "@/attendee/Home";
import Schedule from "@/attendee/Schedule";
import Explore from "@/attendee/Explore";
import ExploreDining from "@/attendee/ExploreDining";
import ExploreVenueDetail from "@/attendee/ExploreVenueDetail";
import ExplorePools from "@/attendee/ExplorePools";
import ExploreActivities from "@/attendee/ExploreActivities";
import ExploreActivityDetail from "@/attendee/ExploreActivityDetail";
import ExploreHotel from "@/attendee/ExploreHotel";
import MyTrip from "@/attendee/MyTrip";
import Photos from "@/attendee/Photos";
import Challenge from "@/attendee/Challenge";
import More from "@/attendee/More";
import MyProfile from "@/attendee/MyProfile";
import Quest from "@/attendee/Quest";

import AdminLayout from "@/admin/AdminLayout";
import Events from "@/admin/Events";
import Attendees from "@/admin/Attendees";
import AttendeePreview from "@/admin/AttendeePreview";
import Groups from "@/admin/Groups";
import ScheduleAdmin from "@/admin/ScheduleAdmin";
import AnnouncementsAdmin from "@/admin/AnnouncementsAdmin";
import TravelImports from "@/admin/Imports";
import TransfersAdmin from "@/admin/TransfersAdmin";
import Overview from "@/admin/Overview";
import DataImports from "@/admin/DataImports";
import TravelMasterUpload from "@/admin/TravelMasterUpload";
import Travel from "@/admin/Travel";
import FlightReconciliation from "@/admin/FlightReconciliation";import ContentSection from "@/admin/ContentSection";
import ExploreAdmin from "@/admin/ExploreAdmin";
import KnowBeforeAdmin from "@/admin/KnowBeforeAdmin";
import Gamification from "@/admin/Gamification";
import QuestAdmin from "@/admin/QuestAdmin";
import Birthdays from "@/admin/Birthdays";
import FutureModule from "@/admin/FutureModule";
import Settings from "@/admin/Settings";

function LoadingScreen() {
  return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--pc-ink))]" data-testid="auth-loading">
      <div className="flex flex-col items-center gap-3 text-[hsl(var(--pc-cream)/0.75)]">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--pc-gold))]" />
        <span className="font-serif text-lg">Preparing your experience…</span>
      </div>
    </div>
  );
}

function AccessDenied() {
  const navigate = useNavigate();
  const { signOut } = useAuthCtx();
  return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] cream-grain px-6">
      <div className="max-w-md text-center" data-testid="manager-access-denied">
        <div className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-[hsl(var(--primary))] text-white mb-5">
          <ShieldX className="h-7 w-7" />
        </div>
        <h1 className="font-serif text-3xl text-[hsl(var(--primary))] mb-2">Access restricted</h1>
        <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">
          The Events Manager is available to authorized event managers only. Your
          account doesn&apos;t currently have manager access.
        </p>
        <div className="mt-7 flex items-center justify-center gap-3">
          <button
            data-testid="access-denied-attendee-button"
            onClick={() => navigate("/app")}
            className="inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--primary))] px-5 py-2.5 text-white font-semibold hover:brightness-110 transition-[filter]"
          >
            <Smartphone className="h-4 w-4" /> Go to attendee app
          </button>
          <button
            data-testid="access-denied-signout-button"
            onClick={signOut}
            className="inline-flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] px-5 py-2.5 font-semibold text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function RootEntry() {
  const { demoMode, clerkLoaded, isSignedIn, profileLoading, hasAccess, defaultDestination } = useAuthCtx();
  if (demoMode) return <Navigate to="/app" replace />;
  if (!clerkLoaded) return <LoadingScreen />;
  if (!isSignedIn) return <SignIn />;
  if (profileLoading) return <LoadingScreen />;
  if (!hasAccess) return <NotAuthorized />;
  // The application decides where the user belongs — no generic chooser.
  if (defaultDestination === "events_manager") return <Navigate to="/manager" replace />;
  return <Navigate to="/app" replace />;
}

function RequireAttendee({ children }) {
  const { demoMode, clerkLoaded, isSignedIn, profileLoading, isAttendee, isManager } = useAuthCtx();
  if (demoMode) return children;
  if (!clerkLoaded || (isSignedIn && profileLoading)) return <LoadingScreen />;
  if (!isSignedIn) return <Navigate to="/" replace />;
  if (!isAttendee) {
    // A manager-only user must NOT gain attendee access via a direct URL.
    if (isManager) return <Navigate to="/manager" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function RequireManager({ children }) {
  const { demoMode, clerkLoaded, isSignedIn, profileLoading, isManager } = useAuthCtx();
  if (demoMode) return children;
  if (!clerkLoaded || (isSignedIn && profileLoading)) return <LoadingScreen />;
  if (!isSignedIn) return <Navigate to="/" replace />;
  if (!isManager) return <AccessDenied />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<RootEntry />} />

              {/* Attendee experience — authorized attendees only */}
              <Route
                path="/app"
                element={
                  <RequireAttendee>
                    <AttendeeLayout />
                  </RequireAttendee>
                }
              >
                <Route index element={<Home />} />
                <Route path="schedule" element={<Schedule />} />
                <Route path="explore" element={<Explore />} />
                <Route path="explore/dining-bars" element={<ExploreDining />} />
                <Route path="explore/dining/:id" element={<ExploreVenueDetail />} />
                <Route path="explore/pools-beach" element={<ExplorePools />} />
                <Route path="explore/activities-excursions" element={<ExploreActivities />} />
                <Route path="explore/activities/:id" element={<ExploreActivityDetail />} />
                <Route path="explore/hotel-essentials" element={<ExploreHotel />} />
                {/* Legacy redirects — preserve old links, no dead ends */}
                <Route path="explore/dining" element={<Navigate to="/app/explore/dining-bars" replace />} />
                <Route path="explore/activities" element={<Navigate to="/app/explore/activities-excursions" replace />} />
                <Route path="explore/hotel" element={<Navigate to="/app/explore/hotel-essentials" replace />} />
                <Route path="explore/transfers" element={<Navigate to="/app/explore/hotel-essentials" replace />} />
                <Route path="explore/destination" element={<Navigate to="/app/explore" replace />} />
                <Route path="explore/maps" element={<Navigate to="/app/explore" replace />} />
                <Route path="trip" element={<MyTrip />} />
                <Route path="photos" element={<Photos />} />
                <Route path="challenge" element={<Challenge />} />
                <Route path="more" element={<More />} />
                <Route path="profile" element={<MyProfile />} />
                <Route path="quest" element={<Quest />} />
              </Route>

              {/* Manager-only READ-ONLY "Preview as Attendee" — full-screen,
                  outside AdminLayout, guarded by RequireManager (backend also
                  enforces manager access on every attendee endpoint it calls). */}
              <Route
                path="/manager/attendees/:attendeeId/preview"
                element={
                  <RequireManager>
                    <AttendeePreview />
                  </RequireManager>
                }
              />

              {/* Events Manager — managers / admins only */}
              <Route
                path="/manager"
                element={
                  <RequireManager>
                    <AdminLayout />
                  </RequireManager>
                }
              >
                <Route index element={<Overview />} />
                <Route path="overview" element={<Overview />} />
                <Route path="events" element={<Events />} />
                <Route path="attendees" element={<Attendees />} />
                <Route path="groups" element={<Groups />} />
                <Route path="schedule" element={<ScheduleAdmin />} />
                <Route path="travel" element={<Travel />} />
                <Route path="flight-reconciliation" element={<FlightReconciliation />} />
                <Route path="gamification" element={<Gamification />} />
                <Route path="quest" element={<QuestAdmin />} />
                <Route path="birthdays" element={<Birthdays />} />
                <Route path="hotel-rooming" element={<FutureModule moduleKey="hotel-rooming" />} />
                <Route path="imports" element={<Navigate to="/manager/data" replace />} />
                <Route path="data" element={<DataImports />} />
                <Route path="pc-master-data" element={<TravelMasterUpload />} />
                <Route path="travel-master" element={<Navigate to="/manager/pc-master-data" replace />} />
                <Route path="transfers" element={<TransfersAdmin />} />
                <Route path="announcements" element={<AnnouncementsAdmin />} />
                <Route path="know-before" element={<KnowBeforeAdmin />} />
                <Route path="budget" element={<FutureModule moduleKey="budget" />} />
                <Route path="explore" element={<ExploreAdmin />} />
                <Route path="hotel-dining" element={<Navigate to="/manager/explore" replace />} />
                <Route path="activities" element={<Navigate to="/manager/explore" replace />} />
                <Route path="photos" element={<ContentSection section="photos" />} />
                <Route path="faqs" element={<ContentSection section="faqs" />} />
                <Route path="contacts" element={<ContentSection section="contacts" />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AppProvider>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
